import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const LEVER_LABELS = {
  after_2: "закрытие после 2 отв.",
  after_3: "закрытие после 3 отв.",
  friendly: "дружелюбный тон",
  direct: "прямой тон",
  question: "CTA-вопрос",
  invite: "CTA-приглашение",
};

const leversStr = (l) =>
  l ? Object.values(l).map((v) => LEVER_LABELS[v] || v).join(" • ") : "—";

const VariantStat = ({ s }) => {
  if (!s) return <span className="text-gray-400">—</span>;
  return (
    <span>
      {s.sessions} сесс. / {s.conversions} заяв. — <b>{(s.rate * 100).toFixed(1)}%</b>
    </span>
  );
};

export default function AdminExperiments() {
  const [experiments, setExperiments] = useState([]);
  const [daily, setDaily] = useState({ rows: [], totals: {} });
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const [expRes, dailyRes] = await Promise.all([
        axios.get(`${API}/admin/ab-experiments`),
        axios.get(`${API}/admin/dialogs-by-date?days=${d}`),
      ]);
      setExperiments(expRes.data.experiments || []);
      setDaily(dailyRes.data || { rows: [], totals: {} });
    } catch (e) {
      toast.error("Не удалось загрузить данные экспериментов");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Загрузка…</div>;
  }

  return (
    <div className="space-y-8 text-black" data-testid="admin-experiments-tab">
      {/* A/B experiments by niche */}
      <div className="bg-white border-2 border-black rounded-lg overflow-hidden">
        <div className="p-4 border-b-2 border-black">
          <h2 className="text-xl font-bold text-black">🧪 A/B-эксперименты по нишам</h2>
          <p className="text-sm text-gray-600 mt-1">
            Автономная оптимизация продажной части промпта. Победитель применяется всем салонам ниши.
          </p>
        </div>
        {experiments.length === 0 ? (
          <p className="text-center text-gray-500 py-8" data-testid="no-experiments">
            Экспериментов пока нет — они создаются автоматически с первым диалогом в нише
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-black" data-testid="experiments-table">
              <thead className="bg-black text-white">
                <tr>
                  <th className="px-3 py-2 text-left">Ниша</th>
                  <th className="px-3 py-2 text-left">Статус</th>
                  <th className="px-3 py-2 text-left">Чемпион</th>
                  <th className="px-3 py-2 text-left">Стат. чемпиона</th>
                  <th className="px-3 py-2 text-left">Претендент</th>
                  <th className="px-3 py-2 text-left">Стат. претендента</th>
                  <th className="px-3 py-2 text-center">Решений</th>
                </tr>
              </thead>
              <tbody>
                {experiments.map((exp, i) => (
                  <tr key={exp.id} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-3 py-2 font-bold">{exp.business_type}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        exp.status === "running"
                          ? "bg-green-100 text-green-700 border border-green-300"
                          : "bg-gray-200 text-gray-700"
                      }`}>
                        {exp.status === "running" ? "идёт" : "завершён"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">{leversStr(exp.champion)}</td>
                    <td className="px-3 py-2 text-xs"><VariantStat s={exp.live_stats?.champion} /></td>
                    <td className="px-3 py-2 text-xs">{leversStr(exp.challenger)}</td>
                    <td className="px-3 py-2 text-xs"><VariantStat s={exp.live_stats?.challenger} /></td>
                    <td className="px-3 py-2 text-center font-bold">{(exp.history || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialogs & conversions by date */}
      <div className="bg-white border-2 border-black rounded-lg overflow-hidden">
        <div className="p-4 border-b-2 border-black flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-black">📅 Диалоги и заявки по датам</h2>
            <p className="text-sm text-gray-600 mt-1">
              Всего за период: <b>{daily.totals?.dialogs || 0}</b> диалогов, <b>{daily.totals?.bookings || 0}</b> заявок,
              конверсия <b>{daily.totals?.conversion || 0}%</b>
            </p>
          </div>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`daily-period-${d}`}
                className={`px-3 py-1 text-xs font-semibold rounded border-2 border-black ${
                  days === d ? "bg-black text-white" : "bg-white text-black hover:bg-gray-100"
                }`}
              >
                {d} дн.
              </button>
            ))}
          </div>
        </div>
        {(daily.rows || []).length === 0 ? (
          <p className="text-center text-gray-500 py-8">Нет данных за период</p>
        ) : (
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm text-black" data-testid="daily-stats-table">
              <thead className="bg-black text-white sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Дата</th>
                  <th className="px-3 py-2 text-right">Диалогов</th>
                  <th className="px-3 py-2 text-right">Заявок</th>
                  <th className="px-3 py-2 text-right">Конверсия</th>
                </tr>
              </thead>
              <tbody>
                {daily.rows.map((r, i) => (
                  <tr key={r.date} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-3 py-2 font-mono">{r.date}</td>
                    <td className="px-3 py-2 text-right">{r.dialogs}</td>
                    <td className="px-3 py-2 text-right font-semibold">{r.bookings}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={r.conversion >= 20 ? "text-green-700 font-bold" : ""}>{r.conversion}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
